/* ============================================
   Shade Showcase

   CENTRE IS THE SINGLE SOURCE OF TRUTH. The row is a native CSS scroll-snap
   scroller — no slider library. Tabs, arrows, card clicks and the Theme
   Editor all do exactly one thing: scroll the row. An IntersectionObserver
   watching a band across the middle of the scroller reports whichever card is
   centred, and setCentred() mirrors that onto the tab strip and the card
   sizes. Nothing writes the active state directly, so there is no duplicate
   bookkeeping to drift out of sync.

   INFINITE LOOP. The Liquid renders the shade set three times — leading copy,
   real copy, trailing copy — and the row opens centred on the middle copy.
   Because every copy is complete, the run of cards either side of a given
   shade is identical in all three, so once the row settles on an outer copy
   correctLoop() can hand .is-active to the real card and reassign scrollLeft
   to it. Same shade, same size, same neighbours: the correction is
   pixel-identical, and the user can keep scrolling in either direction
   forever. This is what `infinite: true` buys in a slider library, without
   the library.

   WHY NO FLIP. The active card grows via a real width/height change (a
   transform would not free or reserve layout space, which makes the gap
   around the active card read differently from the gap between two
   non-active cards). With a slider library that resize has to be instant so
   the library can measure and re-centre — hence the usual hand-rolled
   First-Last-Invert-Play. Here `scroll-snap-type: x mandatory` makes the
   browser re-snap the row by itself, so the grow is just a CSS transition
   and there is nothing to correct in JS.
   ============================================ */

const FLASH_MS = 1800;
const SETTLE_MS = 140;
const DRAG_THRESHOLD_PX = 3;

/* How far one gesture travels, on touch and mouse alike. A release glides to
   the centred card the same way an arrow click does: one card, or two for a
   deliberately strong swipe — judged on speed, or on a drag long enough that
   moving a single card would feel like the gesture was ignored. Never more. */
const SWIPE_MIN_PX = 30;
const SWIPE_STRONG_VELOCITY = 1;
const SWIPE_STRONG_PITCH = 1.5;
const MAX_SWIPE_STEP = 2;

/* Trackpad. A horizontal two-finger gesture is a wheel event, and scroll-snap
   does not constrain wheel momentum the way it constrains a touch fling — so
   without this the browser pans the row across several cards. One step per
   gesture, with a cooldown so a single long gesture cannot machine-gun. */
const WHEEL_MIN_DELTA = 12;
const WHEEL_COOLDOWN_MS = 500;

/* How long the glide itself needs, on top of the card's width transition.
   Snap has to stay out of the way for both: re-engaging while the card is
   still growing snaps the row to a half-grown card, which is the correction
   that reads as "it stopped, then adjusted". */
const GLIDE_MS = 400;

/* How long after revealTab() starts a smooth scroll of the tab strip its own
   scroll events keep arriving. Anything inside that window is ours, not the
   user's — see onTabsScroll. */
const TAB_SCROLL_CLAIM_MS = 600;

/* Shrink the observer root to a band across the middle of the scroller. 20%
   of the scroller width is always wider than the inter-card gap (so the band
   can never fall entirely inside a gap and match nothing) and always narrower
   than the active card (so at rest exactly one card covers it). */
const CENTRE_BAND_MARGIN = '0px -40% 0px -40%';

class ShadeShowcase extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-shade-slider]');
    this.slides = [...this.querySelectorAll('[data-shade-slide]')];
    if (!this.slider || !this.slides.length) return;

    this.tabsWrap = this.querySelector('[data-shade-tabs]');
    this.tabs = [...this.querySelectorAll('[data-shade-tab]')];
    this.swipeHint = this.querySelector('[data-shade-swipe]');
    this.swipeHintLeft = this.querySelector('[data-shade-swipe-left]');
    this.prevButton = this.querySelector('[data-shade-prev]');
    this.nextButton = this.querySelector('[data-shade-next]');
    this.cartDrawer = document.querySelector('cart-drawer');

    // Three complete copies, so the real one starts a third of the way in.
    this.setSize = this.slides.length / 3;
    this.realStart = this.setSize;
    this.centred = this.slides[this.realStart];

    this.activeIndex = 0;
    this.isAdding = false;
    this.drag = null;
    this.dragMoved = false;
    this.tabScrollUntil = 0;
    this.tabsSettleTimer = null;
    this.settleClassTimer = null;
    this.lastWheelStep = 0;
    this.scrolling = false;
    this.chainTimer = null;
    // The tabs currently carrying .is-active — one per copy. Seeded from the
    // server-rendered state so the first sync knows what to clear.
    this.activeTabs = this.tabs.filter((tab) => tab.classList.contains('is-active'));
    // True while a glide is in flight — see the guard in onIntersect.
    this.navigating = false;
    /* --ss-slide comes from the section's own style attribute, so it is set
       whether or not the stylesheet has finished loading. Snap stays off for
       the card's growth plus the glide — releasing it any earlier lets it
       snap to a card that is still mid-resize. */
    this.cardSpeed = parseFloat(getComputedStyle(this.slider).getPropertyValue('--ss-slide')) || 450;
    this.settleHold = this.cardSpeed + GLIDE_MS;
    // null, not false — onTabsResize compares against this to detect a change,
    // and the first run has to apply whichever state it finds.
    this.tabsLoop = null;
    this.flashTimer = null;
    this.settleTimer = null;
    this.centreOverlap = new Map();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Same 749px breakpoint the stylesheet uses, so "mobile" means one thing
    // across both. Viewport-based rather than pointer-based, so the device
    // toolbar behaves like the real phone it is emulating.
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.onTabsClick = this.onTabsClick.bind(this);
    this.onTabsKeydown = this.onTabsKeydown.bind(this);
    this.onTabsScroll = this.onTabsScroll.bind(this);
    this.onSwipeNext = this.onSwipeNext.bind(this);
    this.onSwipePrev = this.onSwipePrev.bind(this);
    this.onSliderClick = this.onSliderClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onTabsResize = this.onTabsResize.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.tabsWrap?.addEventListener('click', this.onTabsClick);
    this.tabsWrap?.addEventListener('keydown', this.onTabsKeydown);
    this.tabsWrap?.addEventListener('scroll', this.onTabsScroll, { passive: true });
    this.swipeHint?.addEventListener('click', this.onSwipeNext);
    this.swipeHintLeft?.addEventListener('click', this.onSwipePrev);
    this.slider.addEventListener('click', this.onSliderClick);
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.slider.addEventListener('pointermove', this.onPointerMove);
    this.slider.addEventListener('pointerup', this.onPointerUp);
    this.slider.addEventListener('pointercancel', this.onPointerCancel);
    /* Touch runs on Touch Events, not on the pointer path above — see the
       comment on onTouchStart. touchmove is the one listener that cannot be
       passive: it calls preventDefault once the gesture is horizontal. */
    this.slider.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.slider.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.slider.addEventListener('touchend', this.onTouchEnd);
    this.slider.addEventListener('touchcancel', this.onTouchEnd);
    // Not passive — the handler calls preventDefault to take horizontal
    // panning away from the browser.
    this.slider.addEventListener('wheel', this.onWheel, { passive: false });
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.addEventListener('submit', this.onSubmit);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    // Open on the middle copy so there are already cards to the left.
    this.jumpTo(this.centred);
    this.slider.classList.add('is-ready');

    this.observer = new IntersectionObserver(this.onIntersect, {
      root: this.slider,
      rootMargin: CENTRE_BAND_MARGIN,
      // Only a fallback now — every real move calls setCentred directly, so
      // one crossing per element is plenty and costs the least per scroll.
      threshold: 0,
    });
    for (const slide of this.slides) this.observer.observe(slide);

    if (this.tabsWrap) {
      this.tabsResizeObserver = new ResizeObserver(this.onTabsResize);
      this.tabsResizeObserver.observe(this.tabsWrap);
      // Run it now rather than waiting for the observer's first delivery, so
      // the strip is parked and the pill centred in the same frame the card
      // row is — otherwise the tabs visibly shift a beat after paint.
      this.syncTabs(this.activeIndex);
      this.onTabsResize();
      /* And again once layout has definitely settled. The call above reads
         widths from inside connectedCallback, which can land before this
         subtree is fully laid out; a short measurement there reports "no
         overflow" and hides both arrows until something else happens to
         re-measure. Re-running costs one frame and makes the opening state
         independent of that timing. */
      requestAnimationFrame(() => this.onTabsResize());
    }
  }

  disconnectedCallback() {
    this.tabsWrap?.removeEventListener('click', this.onTabsClick);
    this.tabsWrap?.removeEventListener('keydown', this.onTabsKeydown);
    this.tabsWrap?.removeEventListener('scroll', this.onTabsScroll);
    this.swipeHint?.removeEventListener('click', this.onSwipeNext);
    this.swipeHintLeft?.removeEventListener('click', this.onSwipePrev);
    this.slider?.removeEventListener('click', this.onSliderClick);
    this.slider?.removeEventListener('scroll', this.onScroll);
    this.slider?.removeEventListener('pointerdown', this.onPointerDown);
    this.slider?.removeEventListener('pointermove', this.onPointerMove);
    this.slider?.removeEventListener('pointerup', this.onPointerUp);
    this.slider?.removeEventListener('pointercancel', this.onPointerCancel);
    this.slider?.removeEventListener('touchstart', this.onTouchStart);
    this.slider?.removeEventListener('touchmove', this.onTouchMove);
    this.slider?.removeEventListener('touchend', this.onTouchEnd);
    this.slider?.removeEventListener('touchcancel', this.onTouchEnd);
    this.slider?.removeEventListener('wheel', this.onWheel);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.removeEventListener('submit', this.onSubmit);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);

    this.observer?.disconnect();
    this.tabsResizeObserver?.disconnect();
    clearTimeout(this.flashTimer);
    clearTimeout(this.settleTimer);
    clearTimeout(this.tabsSettleTimer);
    clearTimeout(this.settleClassTimer);
    clearTimeout(this.chainTimer);
  }

  /* ---------- Centre detection ---------- */

  onIntersect(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.centreOverlap.set(entry.target, entry.intersectionRect.width);
      } else {
        this.centreOverlap.delete(entry.target);
      }
    }

    /* A glide owns the active state until it lands. centreAt() names the
       destination up front, but the row has not moved yet — the OUTGOING card
       is still the one covering the centre band, so acting on this report
       would hand the state straight back to it and only return it to the
       destination halfway through the move. That flip is what makes the tab
       strip run forward, snap back, then forward again. The overlap map above
       still updates throughout, so the first report after the glide lands is
       accurate. */
    /* And nothing happens mid-drag either. Acting here would swap .is-active
       while the finger is still moving, and that resize lays out the whole
       45-card flex row — per frame, on top of the scrollLeft write. That was
       the lag on real phones. The card the drag started on stays grown and
       travels with the row; the swap happens once, in centreAt(), on release.
       Slick behaves the same way: its zoom runs on beforeChange, not during
       the drag. */
    if (this.navigating || this.drag) return;

    let centred = null;
    let widest = 0;
    for (const [slide, width] of this.centreOverlap) {
      if (width > widest) {
        widest = width;
        centred = slide;
      }
    }

    if (centred) this.setCentred(centred);
  }

  /* The centred card may be a loop copy. It still owns .is-active — copies are
     full-fidelity cards — and it still drives the tab strip, because every
     copy carries the real shade index it duplicates. correctLoop() hands the
     state back to the real card once the row settles. */
  setCentred(element) {
    if (element === this.centred) return;

    this.centred?.classList.remove('is-active');
    element.classList.add('is-active');
    this.centred = element;

    const index = Number(element.dataset.shadeSlide);
    if (index === this.activeIndex || Number.isNaN(index)) return;
    this.activeIndex = index;

    this.syncTabs(index);
  }

  /* Matched on the shade index, not the array position — the strip holds three
     copies of every tab, and whichever copy is on screen has to show the
     active state. Only the real copy can take focus. */
  /* Touches only the tabs whose state actually changes — three copies out,
     three in. Walking all of them (45 elements, 135 attribute writes) ran on
     every centred-card change, including mid-scroll, and was enough work to
     show up as stutter. */
  syncTabs(index) {
    for (const tab of this.activeTabs) {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-pressed', 'false');
      tab.setAttribute('tabindex', '-1');
    }

    this.activeTabs = [];
    for (let copy = 0; copy < 3; copy += 1) {
      const tab = this.tabs[copy * this.setSize + index];
      if (!tab) continue;
      tab.classList.add('is-active');
      tab.setAttribute('aria-pressed', 'true');
      if (!tab.hasAttribute('data-shade-tab-clone')) tab.setAttribute('tabindex', '0');
      this.activeTabs.push(tab);
    }

    /* Deliberately not while the row is still moving. This starts a smooth
       scroll on the tab strip, and a second animated scroll running alongside
       the card row's own is the other half of the mid-gesture cost. The
       settle handler runs it once the row stops. */
    if (!this.scrolling) this.revealTab(index);
  }

  /* ---------- Infinite loop ---------- */

  onScroll() {
    this.scrolling = true;
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.scrolling = false;
      // A finger resting mid-gesture is not a settle.
      if (this.drag) return;
      this.correctLoop();
      // Deferred out of syncTabs so it runs once, here, instead of starting a
      // fresh smooth scroll on every centred-card change during the gesture.
      this.revealTab(this.activeIndex);
    }, SETTLE_MS);
  }

  /* Runs once the row has stopped moving. If it settled on a loop copy, hand
     .is-active to the real card of the same shade and put the scroll on it.
     Both are the same shade at the same size with the same neighbours, so
     nothing on screen changes — the row is simply back in the middle copy
     with a full set of cards available in both directions again. */
  correctLoop() {
    if (this.drag || !this.centred) return;

    const pos = Number(this.centred.dataset.shadePos);
    if (pos >= this.realStart && pos < this.realStart + this.setSize) return;

    const real = this.slides[this.realStart + (pos % this.setSize)];
    if (!real || real === this.centred) return;

    this.slider.classList.add('is-jumping');
    this.centred.classList.remove('is-active');
    real.classList.add('is-active');
    this.centred = real;
    this.jumpTo(real);
    // Force the browser to commit the resize and the new scroll position
    // while transitions are still suppressed, so re-enabling them below
    // cannot animate the swap that just happened.
    void this.slider.offsetWidth;
    this.slider.classList.remove('is-jumping');
  }

  jumpTo(slide) {
    this.slider.scrollLeft = slide.offsetLeft + slide.offsetWidth / 2 - this.slider.clientWidth / 2;
  }

  /* ---------- Scrolling ---------- */

  scrollBehavior() {
    return this.reducedMotion.matches ? 'auto' : 'smooth';
  }

  /* Scrolls the row so the card at `pos` lands dead centre. That card is about
     to grow and the current one about to shrink, so the resting geometry is
     not the geometry we can measure right now — applying the delta up front
     lands the scroll exactly, instead of letting the snap engine correct it
     afterwards with a visible second hop. */
  centreAt(pos) {
    const slide = this.slides[Math.max(0, Math.min(pos, this.slides.length - 1))];
    if (!slide) return;

    /* getBoundingClientRect, not offsetLeft/offsetWidth. Those round to whole
       pixels, but the layout is fractional — mobile cards are sized in vw, so
       70vw of a 393px viewport is 275.1px — and the rounding is what left the
       row a few pixels short of centre and gave snap something to correct. */
    const sliderRect = this.slider.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    let centre = slideRect.left + slideRect.width / 2;

    if (this.centred && this.centred !== slide) {
      // This card is about to grow and the current one to shrink, so aim at
      // the geometry the row will have, not the one it has now.
      const growth = this.centred.getBoundingClientRect().width - slideRect.width;
      centre += growth / 2;
      if (Number(this.centred.dataset.shadePos) < Number(slide.dataset.shadePos)) centre -= growth;
    }

    /* Resize now rather than waiting for the observer to notice mid-glide.
       Otherwise the destination computed above is a target that moves while
       the row is still travelling towards it. */
    this.setCentred(slide);

    /* Hold snap off, and hold the observer off, until the glide AND the
       card's growth have both finished. */
    this.navigating = true;
    this.slider.classList.add('is-settling');
    clearTimeout(this.settleClassTimer);
    this.settleClassTimer = setTimeout(() => {
      this.navigating = false;
      this.slider.classList.remove('is-settling');
    }, this.settleHold);

    this.slider.scrollTo({
      left: this.slider.scrollLeft + centre - (sliderRect.left + this.slider.clientWidth / 2),
      behavior: this.scrollBehavior(),
    });
  }

  /* A shade appears once per copy. Going to the nearest occurrence keeps the
     travel short instead of scrolling back across the whole set. */
  centreShade(index) {
    const from = Number(this.centred?.dataset.shadePos ?? this.realStart);
    let best = index;
    for (let copy = 1; copy < 3; copy += 1) {
      const candidate = index + this.setSize * copy;
      if (Math.abs(candidate - from) < Math.abs(best - from)) best = candidate;
    }
    this.centreAt(best);
  }

  /* Keeps the active pill visible without ever scrolling the page — which is
     what scrollIntoView would risk on a strip nested this deep. */
  revealTab(index, instant) {
    const tab = this.nearestTab(index);
    if (!tab || !this.tabsWrap) return;

    /* Align the pill with the centre line the ACTIVE CARD sits on, not with
       the middle of the strip. The swipe badge shares the strip's flex row,
       so the strip is about half the badge's width left of centre — centring
       within it would leave the pill visibly off from the card below it.
       The pill's scale(1.1) is irrelevant here: it transforms about its own
       centre, which is the only thing being measured. */
    const tabRect = tab.getBoundingClientRect();
    const sliderRect = this.slider.getBoundingClientRect();
    const delta = tabRect.left + tabRect.width / 2 - (sliderRect.left + sliderRect.width / 2);

    // Claim the scroll events this is about to emit, so onTabsScroll can tell
    // them apart from a real user swipe of the strip.
    this.tabScrollUntil = performance.now() + TAB_SCROLL_CLAIM_MS;
    this.tabsWrap.scrollTo({
      left: this.tabsWrap.scrollLeft + delta,
      behavior: instant ? 'auto' : this.scrollBehavior(),
    });
  }

  /* A shade has a tab in each of the three copies. Going to whichever one is
     already closest is what makes last -> first read as a single step forward
     instead of a long scroll back across the whole strip. */
  nearestTab(index) {
    if (!this.tabsLoop) return this.tabs[this.realStart + index];

    const centre = this.tabsWrap.scrollLeft + this.tabsWrap.clientWidth / 2;
    let best = null;
    let bestDistance = Infinity;

    for (let copy = 0; copy < 3; copy += 1) {
      const tab = this.tabs[copy * this.setSize + index];
      if (!tab) continue;
      const distance = Math.abs(tab.offsetLeft + tab.offsetWidth / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = tab;
      }
    }

    return best;
  }

  /* Width of one copy of the shades, measured on the real copy — the only one
     that is never taken out of the flow by .is-single. */
  tabSetSpan() {
    const first = this.tabs[this.realStart];
    const last = this.tabs[this.realStart + this.setSize - 1];
    if (!first || !last) return 0;
    return last.offsetLeft + last.offsetWidth - first.offsetLeft;
  }

  /* Distance from a tab to its counterpart one copy along. Only meaningful
     while the copies are in the flow, which is exactly when tabsLoop is on. */
  tabPeriod() {
    const first = this.tabs[this.realStart];
    const next = this.tabs[this.realStart + this.setSize];
    if (!first || !next) return 0;
    return next.offsetLeft - first.offsetLeft;
  }

  /* Keeps the strip parked in the middle copy so there is always a full run of
     tabs in both directions. The strip is periodic, so a shift of exactly one
     period is pixel-identical — nothing moves on screen, the runway is just
     restored. `park` is the initial placement, which has no previous position
     to preserve. */
  normaliseTabScroll(park) {
    if (!this.tabsLoop || !this.tabsWrap) return;

    const period = this.tabPeriod();
    if (!period) return;

    const left = this.tabsWrap.scrollLeft;
    let next = left;

    if (park) next = period + (left % period);
    else if (left < period * 0.5) next = left + period;
    else if (left > period * 1.5) next = left - period;

    if (next === left) return;
    this.tabScrollUntil = performance.now() + TAB_SCROLL_CLAIM_MS;
    this.tabsWrap.scrollLeft = next;
  }

  /* ---------- Tabs ---------- */

  onTabsClick(event) {
    const tab = event.target.closest('[data-shade-tab]');
    if (!tab) return;
    this.centreShade(Number(tab.dataset.shadeTab));
  }

  onTabsKeydown(event) {
    // Shade count, not tab count — the strip holds three copies of each.
    const last = this.setSize - 1;
    let next = this.activeIndex;

    switch (event.key) {
      case 'ArrowLeft':
        next = next === 0 ? last : next - 1;
        break;
      case 'ArrowRight':
        next = next === last ? 0 : next + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.centreShade(next);
    // Focus the real copy — the loop copies are aria-hidden and untabbable.
    this.tabs[this.realStart + next]?.focus();
  }

  onTabsScroll() {
    // Always schedule the loop correction, including for our own scrolls —
    // revealTab() can leave the strip parked in an outer copy.
    clearTimeout(this.tabsSettleTimer);
    this.tabsSettleTimer = setTimeout(() => this.normaliseTabScroll(false), SETTLE_MS);
  }

  onTabsResize() {
    if (!this.tabsWrap) return;

    // Measured against ONE copy: the strip holds three, so its own scrollWidth
    // always overflows and would claim a two-shade section needs looping.
    const overflows = this.tabSetSpan() > this.tabsWrap.clientWidth + 4;

    if (overflows !== this.tabsLoop) {
      this.tabsLoop = overflows;
      this.tabsWrap.classList.toggle('is-single', !overflows);
      if (overflows) this.normaliseTabScroll(true);
    }

    /* Put the active pill on the card's centre line. syncTabs only fires when
       the shade CHANGES, so without this the strip would open with the first
       pill pinned to its left edge instead of above the card it belongs to. */
    this.revealTab(this.activeIndex, true);

    /* Both arrows stay up for as long as the strip has more shades than fit.
       The reference hides each one at its own end of the strip, but ours
       loops — there is no first or last tab to arrive at, so either
       direction is always available. */
    if (this.swipeHint) this.swipeHint.hidden = !overflows;
    if (this.swipeHintLeft) this.swipeHintLeft.hidden = !overflows;
  }

  /* Clicking an arrow steps the strip by ONE tab: it finds the nearest tab
     cut off past that edge and brings just that one into view, rather than
     scrolling by a fixed pixel amount. Uses the strip's own scrollTo instead
     of scrollIntoView, which would be free to scroll the page as well. */
  onSwipeClick(forward) {
    if (!this.tabsWrap) return;

    const wrapRect = this.tabsWrap.getBoundingClientRect();
    let target = null;

    for (const tab of this.tabs) {
      const rect = tab.getBoundingClientRect();
      if (forward) {
        // First tab past the right edge. Strictly past, not flush with it —
        // a tab just aligned to the edge sits within a pixel of it, and a
        // loose threshold keeps re-picking that same tab so the next click
        // does nothing.
        if (rect.right > wrapRect.right + 2) {
          target = tab;
          break;
        }
      } else if (rect.left < wrapRect.left - 2) {
        // Keep overwriting: the LAST tab still cut off past the left edge is
        // the one nearest the visible area, so it is the right "previous".
        target = tab;
      }
    }

    if (!target) return;

    const rect = target.getBoundingClientRect();
    const delta = forward ? rect.right - wrapRect.right : rect.left - wrapRect.left;

    this.tabScrollUntil = performance.now() + TAB_SCROLL_CLAIM_MS;
    this.tabsWrap.scrollTo({
      left: this.tabsWrap.scrollLeft + delta,
      behavior: this.scrollBehavior(),
    });
  }

  onSwipeNext() {
    this.onSwipeClick(true);
  }

  onSwipePrev() {
    this.onSwipeClick(false);
  }

  /* ---------- Arrows ---------- */

  onPrevClick() {
    this.centreAt(Number(this.centred?.dataset.shadePos ?? this.realStart) - 1);
  }

  onNextClick() {
    this.centreAt(Number(this.centred?.dataset.shadePos ?? this.realStart) + 1);
  }

  /* ---------- Card clicks and drag ---------- */

  onSliderClick(event) {
    /* The buy pill is a control sitting on top of the card, not part of it,
       and the drag-suppression latch below must never reach it. Its button is
       type="submit", so preventDefault on the CLICK cancels the form
       submission outright — no submit event, so onSubmit never runs. That is
       what made the first Add to cart tap after a swipe do nothing on a real
       device: a swipe travels past the browser's tap slop, so no click is
       fired when the finger lifts and the latch is still set when the next
       tap arrives. */
    if (event.target.closest('[data-shade-atc-form]')) return;

    if (this.dragMoved) {
      // A drag that ended over a card must not also follow its link.
      this.dragMoved = false;
      event.preventDefault();
      return;
    }

    const slide = event.target.closest('[data-shade-slide]');
    if (!slide) return;

    // The centred card's link navigates to the product; every other card
    // uses the click to bring itself to the centre instead.
    if (slide === this.centred) return;

    event.preventDefault();
    this.centreAt(Number(slide.dataset.shadePos));
  }

  /* The gesture itself, in three input-agnostic steps. Mouse and pen arrive
     via the pointer handlers below; touch arrives via the touch handlers,
     because iOS Safari will not let the pointer path finish — see
     onTouchStart. Both feed the same three calls, so a swipe behaves
     identically whatever produced it.

     The whole point is that this drag has no inertia: a flick moves the row by
     one card rather than by however far the browser's momentum decides to
     carry it. Native horizontal scrolling is off (touch-action: pan-y in the
     CSS), so these are the only thing that moves the row. */
  startGesture(x, y, lockVertical) {
    /* A hand on the row ends any glide in flight, and restores snap so the
       gesture gets its one-card stop instead of free momentum. */
    clearTimeout(this.settleClassTimer);
    clearTimeout(this.chainTimer);
    this.slider.classList.remove('is-settling');

    // A previous drag whose click never landed must not swallow this one.
    this.dragMoved = false;
    // A hand on the row takes over from any glide still in flight, so the
    // active state tracks the finger again instead of staying pinned.
    this.navigating = false;
    this.drag = {
      startX: x,
      startY: y,
      startPos: Number(this.centred?.dataset.shadePos ?? this.realStart),
      startTime: performance.now(),
      moved: false,
      lockVertical,
    };
    this.slider.classList.add('is-dragging');
  }

  /* Deliberately does not move the row. A gesture is measured here and acted
     on once, in endGesture — the row then glides to the centred card exactly
     the way an arrow click or a trackpad swipe does. Following the finger 1:1
     and then gliding gave touch a different feel from every other input, and
     it was the only path writing scrollLeft every frame.

     Returns true on the single frame the gesture is confirmed horizontal, so
     the pointer path can capture exactly once. */
  moveGesture(x, y) {
    if (!this.drag || this.drag.moved) return false;

    const distance = Math.abs(x - this.drag.startX);
    const vertical = Math.abs(y - this.drag.startY);

    /* Direction lock, touch only. A mostly-vertical gesture is the page being
       scrolled, not the row being swiped, so let go of it entirely — otherwise
       the release below would step a card every time a thumb brushed the row
       on the way down the homepage. A mouse cannot scroll the page by
       dragging, so there is nothing to hand back and the lock would only make
       a drag that set off slightly downwards feel like it was ignored. */
    if (this.drag.lockVertical && vertical > distance && vertical > DRAG_THRESHOLD_PX) {
      this.cancelGesture();
      return false;
    }

    if (distance <= DRAG_THRESHOLD_PX) return false;
    this.drag.moved = true;
    return true;
  }

  endGesture(x) {
    if (!this.drag) return;

    const { startPos, startX, startTime, moved } = this.drag;
    this.dragMoved = moved;
    this.drag = null;
    this.slider.classList.remove('is-dragging');

    // Not a drag — a tap. Snap is back on and nothing moved.
    if (!moved) return;

    const distance = x - startX;
    const travelled = Math.abs(distance);

    // Too small to be a swipe. Leave snap to pull the row back where it was.
    if (travelled < SWIPE_MIN_PX) return;

    /* The step count is decided HERE, not by where the finger happened to end
       up — landing on whichever card the drag reached is what let a single
       long drag walk across the row.

       On mobile it is always exactly one card, however hard the swipe: a
       thumb flick is far less precise than a mouse drag, so the two-card
       case fired when it was not wanted. Desktop keeps it. */
    const velocity = travelled / Math.max(1, performance.now() - startTime);
    const pitch = this.slidePitch();
    const strong =
      !this.mobileQuery.matches &&
      (velocity >= SWIPE_STRONG_VELOCITY || (pitch && travelled > pitch * SWIPE_STRONG_PITCH));
    const direction = distance < 0 ? 1 : -1;

    this.centreAt(startPos + direction);

    /* A strong swipe advances two cards, but as two SEPARATE moves — the
       first card comes to centre and settles, then the second does. Gliding
       straight across both in one motion is what made a hard swipe feel
       different from the same gesture on a trackpad, which fires one card
       per wheel event and so always pauses in between. */
    if (strong) this.queueStep(direction, MAX_SWIPE_STEP - 1);
  }

  /* True when a gesture starts on the buy pill, which is a control rather than
     a grab handle — the row must not move under a tap meant for it.

     Clearing the latch on the way past matters: this is the one entry point
     that returns before startGesture, so without it a stale latch from the
     preceding swipe would survive into the very click this tap is about to
     produce. onSliderClick ignores pill clicks anyway, so the two together are
     belt and braces — but the latch is what tracks reality, and leaving it set
     here would also swallow the NEXT tap on a side card. */
  startsOnBuyPill(target) {
    if (!target.closest('[data-shade-atc-form]')) return false;
    this.dragMoved = false;
    return true;
  }

  /* Abandons a gesture without navigating — snap pulls the row back to where
     it was. Used by the direction lock and by a cancelled pointer. */
  cancelGesture() {
    if (!this.drag) return;
    this.drag = null;
    this.dragMoved = false;
    this.slider.classList.remove('is-dragging');
  }

  /* ---------- Mouse and pen ---------- */

  /* Touch is excluded on purpose. iOS Safari hands a touch to its own scroll
     recognizer the moment it starts panning and fires pointercancel — and it
     does that even where touch-action forbids the pan it thinks it is
     starting, which a real thumb swipe always triggers because it is never
     perfectly horizontal. The gesture was therefore abandoned before
     pointerup, and since touch-action: pan-y also means the browser never
     scrolls the row itself, nothing at all moved on iOS. Touch Events are not
     taken away like this, so touch goes through onTouchStart below. */
  onPointerDown(event) {
    if (event.pointerType === 'touch') return;
    if (this.startsOnBuyPill(event.target)) return;
    this.startGesture(event.clientX, event.clientY, false);
  }

  onPointerMove(event) {
    if (event.pointerType === 'touch') return;
    /* Capture only once this is genuinely a drag, never on pointerdown.
       A captured pointer sends the follow-up click to the capture target
       instead of the element under the cursor, so capturing up front meant a
       plain click on the centred card never reached its <a> and never opened
       the product. Capturing here still keeps the drag alive if the pointer
       leaves the scroller mid-gesture. */
    if (this.moveGesture(event.clientX, event.clientY)) {
      this.slider.setPointerCapture?.(event.pointerId);
    }
  }

  onPointerUp(event) {
    if (event.pointerType === 'touch' || !this.drag) return;
    this.releasePointer(event.pointerId);
    this.endGesture(event.clientX);
  }

  /* A gesture the browser took over for vertical panning. */
  onPointerCancel(event) {
    if (event.pointerType === 'touch' || !this.drag) return;
    this.releasePointer(event.pointerId);
    this.cancelGesture();
  }

  releasePointer(pointerId) {
    if (this.slider.hasPointerCapture?.(pointerId)) {
      this.slider.releasePointerCapture(pointerId);
    }
  }

  /* ---------- Touch ---------- */

  onTouchStart(event) {
    // A second finger is a pinch or a two-handed hold, not a swipe.
    if (event.touches.length > 1) {
      this.cancelGesture();
      return;
    }
    if (this.startsOnBuyPill(event.target)) return;

    const [touch] = event.touches;
    this.startGesture(touch.clientX, touch.clientY, true);
  }

  onTouchMove(event) {
    if (!this.drag) return;
    if (event.touches.length > 1) {
      this.cancelGesture();
      return;
    }

    const [touch] = event.touches;
    this.moveGesture(touch.clientX, touch.clientY);

    /* Once the direction lock has confirmed the gesture is horizontal, the row
       owns it. touch-action: pan-y already stops the browser panning the row,
       but preventing the default here is what stops iOS reclassifying the same
       fingertip as the start of a page scroll partway through — the reclassify
       is what used to kill the gesture. A mostly-vertical gesture never gets
       here: moveGesture has already released it back to the page. */
    if (this.drag?.moved && event.cancelable) event.preventDefault();
  }

  onTouchEnd(event) {
    if (!this.drag) return;

    // touchcancel carries no useful position — treat it as an abandoned drag.
    const [touch] = event.changedTouches;
    if (event.type === 'touchcancel' || !touch) {
      this.cancelGesture();
      return;
    }

    this.endGesture(touch.clientX);
  }

  /* Chains the remaining cards of a multi-card swipe, one per card-transition
     length, so each lands and settles at centre before the next sets off.
     Reads the centred position when the timer fires rather than precomputing
     it, so a loop correction landing in between is accounted for. */
  queueStep(direction, remaining) {
    clearTimeout(this.chainTimer);
    if (remaining < 1) return;

    this.chainTimer = setTimeout(() => {
      const from = Number(this.centred?.dataset.shadePos ?? this.realStart);
      this.centreAt(from + direction);
      this.queueStep(direction, remaining - 1);
    }, this.cardSpeed);
  }

  /* Distance from one card to the next, measured on two cards in the leading
     copy — both are always inactive, so this is the resting pitch rather than
     a gap distorted by whichever card is currently grown. */
  slidePitch() {
    const first = this.slides[0];
    const second = this.slides[1];
    if (!first || !second) return 0;
    return second.offsetLeft - first.offsetLeft;
  }

  /* Trackpad two-finger swipe. Steps exactly one card per gesture, the same
     way the reference does — the browser's own horizontal panning has
     momentum and would carry the row several cards. */
  onWheel(event) {
    // Vertical belongs to the page.
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) return;

    event.preventDefault();
    if (Math.abs(event.deltaX) < WHEEL_MIN_DELTA) return;

    const now = performance.now();
    if (now - this.lastWheelStep < WHEEL_COOLDOWN_MS) return;
    this.lastWheelStep = now;

    const from = Number(this.centred?.dataset.shadePos ?? this.realStart);
    this.centreAt(from + (event.deltaX > 0 ? 1 : -1));
  }

  /* ---------- Theme editor ---------- */

  onBlockSelect(event) {
    const tab = event.target?.closest?.('[data-shade-tab]');
    if (!tab || !this.contains(tab)) return;
    this.centreShade(Number(tab.dataset.shadeTab));
  }

  /* ---------- Add to cart ---------- */

  onSubmit(event) {
    const form = event.target.closest('[data-shade-atc-form]');
    if (!form) return;
    event.preventDefault();
    this.addToCart(form);
  }

  async addToCart(form) {
    if (this.isAdding) return;

    const button = form.querySelector('[data-shade-atc]');
    const variantInput = form.querySelector('input[name="id"]');
    if (!button || !variantInput || button.disabled) return;

    const variantId = String(variantInput.value);
    const labels = button.dataset;

    this.isAdding = true;
    button.classList.add('is-adding');
    button.textContent = labels.labelAdding;

    try {
      const cart = await CartAPI.get();
      const alreadyInCart = (cart.items ?? []).some((item) => String(item.variant_id) === variantId);

      if (alreadyInCart) {
        // Present already — confirm and open the drawer, but never add a
        // second line item or bump the quantity.
        this.flash(button, labels.labelIncart, 'is-incart', labels.labelDefault);
        this.cartDrawer?.openCart();
        return;
      }

      const sectionId = this.cartDrawer?.dataset.sectionId ?? 'cart-drawer';
      const formData = new FormData(form);
      formData.set('sections', sectionId);
      formData.set('sections_url', window.location.pathname);
      formData.set('properties[is_app]', 'true');

      const data = await CartAPI.add(formData);

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shade-showcase', cartData: data });
      }

      this.flash(button, labels.labelAdded, 'is-added', labels.labelDefault);
      this.cartDrawer?.openCart();
      this.cartDrawer?.renderContents(data);
    } catch (error) {
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartError, { source: 'shade-showcase', error });
      }
      const message = error?.description ? labels.labelUnavailable : labels.labelError;
      this.flash(button, message, 'is-error', labels.labelDefault);
    } finally {
      this.isAdding = false;
      button.classList.remove('is-adding');
    }
  }

  flash(button, message, stateClass, restoreLabel) {
    button.classList.add(stateClass);
    button.textContent = message;

    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => {
      button.classList.remove(stateClass);
      button.textContent = restoreLabel;
    }, FLASH_MS);
  }
}

if (!customElements.get('shade-showcase')) {
  customElements.define('shade-showcase', ShadeShowcase);
}
